/**
 * Relative-time formatting for run freshness ("Analyzed 2h ago").
 *
 * Pure, dependency-free. Returns a short, human-friendly relative string and a
 * staleness flag the UI can use to badge a brief as out of date.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/**
 * A short relative time string for an ISO timestamp (or epoch ms).
 * e.g. "just now", "5m ago", "2h ago", "3d ago", "2w ago", "May 4".
 * Returns "" for an empty/invalid input so callers can omit the line cleanly.
 */
export function relativeTime(iso: string | number | null | undefined, now: number = Date.now()): string {
  if (iso === null || iso === undefined || iso === "") return "";
  const then = typeof iso === "number" ? iso : Date.parse(iso);
  if (!Number.isFinite(then)) return "";
  const diff = now - then;

  if (diff < 0) return "just now"; // clock skew - never show a negative age
  if (diff < 45 * 1000) return "just now";
  if (diff < HOUR) {
    const m = Math.round(diff / MINUTE);
    return `${m}m ago`;
  }
  if (diff < DAY) {
    const h = Math.round(diff / HOUR);
    return `${h}h ago`;
  }
  if (diff < WEEK) {
    const d = Math.round(diff / DAY);
    return `${d}d ago`;
  }
  if (diff < 5 * WEEK) {
    const w = Math.round(diff / WEEK);
    return `${w}w ago`;
  }
  // Older than ~a month - show an absolute, locale-aware short date.
  try {
    return new Date(then).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return new Date(then).toDateString();
  }
}

/**
 * A short relative time string for a FUTURE ISO timestamp (or epoch ms).
 * e.g. "in a moment", "in 5 minutes", "in about 2 hours", "tomorrow".
 *
 * `relativeTime` only describes the past (it returns "just now" for any future
 * instant), so the daily-allowance reset time - which is always ahead of now -
 * needs its own forward-looking formatter. Returns "" for an empty/invalid
 * input, and gracefully reads "soon" once the reset moment has passed.
 */
export function relativeFuture(iso: string | number | null | undefined, now: number = Date.now()): string {
  if (iso === null || iso === undefined || iso === "") return "";
  const then = typeof iso === "number" ? iso : Date.parse(iso);
  if (!Number.isFinite(then)) return "";
  const diff = then - now;

  if (diff <= 0) return "soon"; // the reset moment has already arrived
  if (diff < 60 * 1000) return "in a moment";
  if (diff < HOUR) {
    const m = Math.max(1, Math.round(diff / MINUTE));
    return `in ${m} ${m === 1 ? "minute" : "minutes"}`;
  }
  if (diff < DAY) {
    const h = Math.max(1, Math.round(diff / HOUR));
    return `in about ${h} ${h === 1 ? "hour" : "hours"}`;
  }
  const d = Math.round(diff / DAY);
  if (d <= 1) return "tomorrow";
  return `in ${d} days`;
}

/**
 * Whether a run is "stale" - older than the given threshold (default 24h).
 * Used to badge briefs/cards so the user knows when to refresh.
 */
export function isStale(iso: string | number | null | undefined, thresholdMs: number = DAY, now: number = Date.now()): boolean {
  if (iso === null || iso === undefined || iso === "") return false;
  const then = typeof iso === "number" ? iso : Date.parse(iso);
  if (!Number.isFinite(then)) return false;
  return now - then > thresholdMs;
}

/** A longer, absolute timestamp for tooltips / detail views. */
export function absoluteTime(iso: string | number | null | undefined): string {
  if (iso === null || iso === undefined || iso === "") return "";
  const then = typeof iso === "number" ? iso : Date.parse(iso);
  if (!Number.isFinite(then)) return "";
  try {
    return new Date(then).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return new Date(then).toString();
  }
}
